/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Mock LSP → Monaco bridge.
 *
 * Pure helper that fetches completion items from a `LanguageServiceProvider`
 * (the in-process mock LSP) and translates them into Monaco-shaped suggestions.
 * Only consumed by `mockResolver` — the real-engine resolvers build candidates
 * directly via `deriveCompletionContext → buildCandidates → rankCandidates`.
 * This module preserves the LSP-translation behavior (server-trigger
 * normalization, `()` → `$1)` snippet upgrade, `textEdit` range conversion,
 * contextual keyword fallback via `sortCompletionItems`) for the mock engine's
 * deterministic E2E completions.
 *
 * No Monaco runtime imports — the Monaco namespace flows in through
 * `OrchestratorResolverInput.monaco` so the module stays unit-testable in
 * Node.
 */

import { convertCompletionItemKind } from '@/features/workspace/common/utils/completionItemKindConverter';
import { sortCompletionItems } from '@/features/workspace/common/utils/sqlCompletionSorter';
import type { LanguageServiceCompletionContext } from '@/features/connections/services/language-services/languageServiceProvider';
import { noteCompletionIoWait } from '@/features/intellisense/telemetry/providerCost';

import type { OrchestratorResolverInput } from './types';

const SERVER_TRIGGER_CHARS = ['.', ':', '\\', '[', '"', ' '];

export async function fetchMockLspSuggestions(
  input: OrchestratorResolverInput,
): Promise<readonly any[]> {
  const {
    model,
    position,
    documentUri,
    languageServiceProvider,
    monaco,
    triggerKind,
    triggerCharacter,
    statementTextBeforeCursor,
  } = input;

  const wordInfo = model.getWordUntilPosition(position);

  let lspItems: any[] = [];
  try {
    // Document sync + debounce timer below wait on real I/O (provider-cost contract).
    noteCompletionIoWait();
    const currentText = model.getValue();
    await languageServiceProvider.updateDocument(documentUri, currentText);
    // 50ms debounce so LSPs that ingest text on a separate channel have a
    // chance to apply the document before completion runs. Mirrors the
    // pre-orchestrator inline behavior in `SqlMonaco.tsx`.
    await new Promise((resolve) => setTimeout(resolve, 50));

    const triggerChar = triggerCharacter ?? '';
    const isServerTrigger = !!triggerChar && SERVER_TRIGGER_CHARS.includes(triggerChar);
    const effectiveTriggerKind = isServerTrigger ? 2 : (triggerKind ?? 1);
    const effectiveTriggerChar = isServerTrigger ? triggerChar : undefined;

    const completionContext: LanguageServiceCompletionContext = {
      triggerKind: effectiveTriggerKind === 2 ? 2 : 1,
    };
    if (effectiveTriggerChar) {
      completionContext.triggerCharacter = effectiveTriggerChar;
    }

    const completionResult = await languageServiceProvider.getCompletionItems(
      documentUri,
      { line: position.lineNumber - 1, character: position.column - 1 },
      completionContext,
    );

    lspItems = completionResult?.items ?? [];
  } catch {
    return [];
  }

  const sortedItems = sortCompletionItems(lspItems, statementTextBeforeCursor);

  const defaultRange = {
    startLineNumber: position.lineNumber,
    startColumn: wordInfo.startColumn,
    endLineNumber: position.lineNumber,
    endColumn: wordInfo.endColumn,
  };

  return sortedItems.map((item: any, index: number) =>
    mapLspItemToMonaco(item, index, defaultRange, monaco),
  );
}

function toMonacoRange(range: any) {
  return {
    startLineNumber: (range?.start?.line ?? 0) + 1,
    startColumn: (range?.start?.character ?? 0) + 1,
    endLineNumber: (range?.end?.line ?? 0) + 1,
    endColumn: (range?.end?.character ?? 0) + 1,
  };
}

export function mapLspItemToMonaco(
  item: any,
  index: number,
  defaultRange: {
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
  },
  monaco: any,
): any {
  const rawLabel = item.label;
  const label = typeof rawLabel === 'string' ? rawLabel : (rawLabel?.label ?? '');

  let insertText = item.insertText || label;
  let range: any = defaultRange;

  const textEdit = item.textEdit;
  if (textEdit?.newText != null) {
    insertText = textEdit.newText;
    if (textEdit.range) {
      range = toMonacoRange(textEdit.range);
    } else if (textEdit.insert && textEdit.replace) {
      range = {
        insert: toMonacoRange(textEdit.insert),
        replace: toMonacoRange(textEdit.replace),
      };
    }
  }

  let insertTextRules =
    item.insertTextFormat === 2
      ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet
      : undefined;

  // Trailing `()` → `$1)` snippet so the cursor lands inside the parens after
  // insertion (replicates the legacy SqlMonaco.tsx behavior).
  if (!insertTextRules && insertText.endsWith('()')) {
    insertText = `${insertText.slice(0, -1)}$1)`;
    insertTextRules = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;
  }

  const triggerSignatureHelp = insertText.includes('(')
    ? { id: 'editor.action.triggerParameterHints', title: '' }
    : undefined;

  return {
    label,
    kind: convertCompletionItemKind(monaco, item.kind),
    detail: item.detail || '',
    documentation: item.documentation?.value || item.documentation || '',
    insertText,
    ...(insertTextRules ? { insertTextRules } : {}),
    sortText: item.sortText || `9_${String(index).padStart(5, '0')}_${label}`,
    preselect: item.preselect === true,
    filterText: item.filterText || label,
    range,
    ...(triggerSignatureHelp ? { command: triggerSignatureHelp } : {}),
  };
}
